import { type Routine, useStores } from '@littlebird/sdk';
import {
  deviceCount,
  isRunWaiting,
  type WaitingDevice,
} from '@littlebird/ui-utils';
import { observer } from 'mobx-react-lite';

import { Button, Text } from '@/components/ui';
import { XStack } from '@/components/ui/layout/layout';
import { DeviceIcon } from '@/features/routines/components/device-icon';
import {
  formatDeviceTime,
  RESTART_WAITING_RUN_COPY,
} from '@/features/routines/components/waiting-run-copy';
import { getRoutineActionsWithLatestRun } from '@/features/routines/routine-action-state';

export const RestartWaitingRunNote = observer(function RestartWaitingRunNote({
  routine,
  disabled,
  onRestart,
}: {
  routine: Routine;
  disabled: boolean;
  /** Gets the devices the run waits for, which the confirmation names */
  onRestart: (waitingDevices: readonly WaitingDevice[]) => void;
}) {
  const { reports, routines } = useStores();
  const latestRun = reports.latestByTemplateId.get(routine.referenceId);
  if (!latestRun || !isRunWaiting(latestRun)) return null;
  const actions = getRoutineActionsWithLatestRun(routine, reports);
  const waitingDevices = latestRun.waitingOn;
  const [waitingDevice] = waitingDevices;

  return (
    <XStack className="flex-wrap items-center gap-x-3 gap-y-2.5 border-b border-border pb-3.5">
      <XStack className="min-w-0 flex-1 basis-64 items-start gap-2.5">
        <span className="flex h-5 items-center">
          <DeviceIcon
            platform={waitingDevice.platform}
            className="size-3.5 animate-pulse text-foreground-muted motion-reduce:animate-none"
          />
        </span>
        <Text className="text-[13px] leading-5 text-foreground-muted">
          A run from {formatDeviceTime(latestRun.scheduledAt)} is waiting for{' '}
          {waitingDevices.length === 1 ? (
            <span className="font-mono text-xs [overflow-wrap:anywhere]">
              {waitingDevice.deviceSlug}
            </span>
          ) : (
            deviceCount(waitingDevices.length)
          )}
          . Saving here does not change it; it finishes with the instructions it
          started with.
        </Text>
      </XStack>
      {actions.permissions.canRun && (
        <Button
          variant="outline"
          size="sm"
          disabled={
            disabled ||
            routines.isOperationPending(
              'restart-waiting-run',
              routine.referenceId
            )
          }
          onClick={() => onRestart(waitingDevices)}
        >
          {RESTART_WAITING_RUN_COPY.button}
        </Button>
      )}
    </XStack>
  );
});
